import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Calendar, Clock, Zap, ArrowRight } from 'lucide-react';
import type { Agency } from '@/types';
import { formatDate } from '@/lib/utils';

interface PackageSummaryProps {
  agency: Agency;
}

export function PackageSummary({ agency }: PackageSummaryProps) {
  const packageConfig = {
    starter: { 
      name: 'Starter', 
      color: 'bg-slate-500', 
      icon: '🚀',
      description: 'Perfect for small projects'
    },
    growth: { 
      name: 'Growth', 
      color: 'bg-blue-500', 
      icon: '📈',
      description: 'Best for growing agencies'
    },
    sprint: { 
      name: 'Sprint', 
      color: 'bg-purple-500', 
      icon: '⚡',
      description: 'For high-velocity teams'
    },
  };

  const pkg = packageConfig[agency.packageType];

  return (
    <Card 
      title="Package Summary" 
      subtitle="Your current plan and renewal details"
    >
      <div className="space-y-4">
        {/* Plan badge */}
        <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="w-14 h-14 rounded-xl bg-white border-2 border-slate-200 flex items-center justify-center text-3xl shadow-sm">
            {pkg.icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-slate-900 text-lg">{pkg.name}</h4>
              <Badge variant="success" size="sm">Active</Badge>
            </div>
            <p className="text-sm text-slate-500">{pkg.description}</p>
          </div>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-600">Monthly Hours</span>
            </div>
            <p className="text-lg font-bold text-slate-900">{agency.monthlyHours}</p>
            <p className="text-xs text-slate-500">hours included</p>
          </div>

          <div className="p-3 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-600">Extra Hours</span>
            </div>
            <p className="text-lg font-bold text-slate-900">${agency.extraHourRate}</p>
            <p className="text-xs text-slate-500">per hour</p>
          </div>

          <div className="p-3 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-green-600" />
              <span className="text-xs font-medium text-green-600">Rollover</span>
            </div>
            <p className="text-lg font-bold text-slate-900">{agency.rolloverHours}</p>
            <p className="text-xs text-slate-500">unused hours</p>
          </div>

          <div className="p-3 bg-amber-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-600">Renews</span>
            </div>
            <p className="text-lg font-bold text-slate-900">{formatDate(new Date(agency.renewalDate))}</p>
            <p className="text-xs text-slate-500">next billing</p>
          </div>
        </div>

        {/* Upgrade CTA */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-semibold text-slate-900">Need more capacity?</h5>
              <p className="text-sm text-slate-600">Upgrade to unlock more hours</p>
            </div>
            <Button size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
              Upgrade
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
